import { useEffect, useMemo, useState } from "react";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { useTranslation } from 'react-i18next';
import { DateTime } from "luxon";
import { Group } from "../../models/interfaces/group";
import { Tag } from "../../models/interfaces/tag";
import ReportDataUtil from "../../utilities/report-data-util";
import { FilterState } from "../../models/interfaces/filter-state";
import { ReportDynamicComplianceData } from "../../models/interfaces/reports/report-dynamic-compliance-data";
import { Button } from "../button/button";
import { Loader } from "../loader/loader";
import ReportFilterMenu from "../report-filter-menu/report-filter-menu";
import { ReportFilterDefinition } from "../../models/interfaces/report-filter-definition";
import { ReportFilterType } from "../../models/enumerations/report-filter-type";
import Accordion from "../accordion/accordion";
import ReportDynamicGrid from "./report-dynamic-grid";
import CircleIcon from '@mui/icons-material/Circle';

const COMPONENT_CLASS = "c-report-compliance-dynamic";

const DEFAULT_FILTERS: FilterState = {
    groupIds: [],
    tagIds: [],
};

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ReportComplianceDynamic: React.FC = () => {
    const [isLoading, setIsLoading] = useState(true);
    const { t } = useTranslation();
    const { state } = useAuthState();
    const [reportData, setReportData] = useState<ReportDynamicComplianceData | null>(null);
    const [filteredReportData, setFilteredReportData] = useState<ReportDynamicComplianceData | null>(null);
    const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [groups, setGroups] = useState<Group[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const completedAssessmentPercent =
        filteredReportData && filteredReportData.totalUsers > 0
            ? Math.max(0, Math.round((filteredReportData.usersAssessment / filteredReportData.totalUsers) * 100))
            : 0;
    const filterDefinitions: ReportFilterDefinition[] = useMemo(() => [
        {
            key: "groupIds",
            label: t('reports.filterOptions.groups'),
            placeholder: t('reports.filterOptions.allGroups'),
            type: ReportFilterType.MultiSelect,
            options: groups.map(g => ({
                label: g.name,
                value: g.id
            }))
        },
        {
            key: "tagIds",
            label: t('reports.filterOptions.tags'),
            placeholder: t('reports.filterOptions.allTags'),
            type: ReportFilterType.MultiSelect,
            options: tags.map(t => ({
                label: t.name,
                value: t.id
            }))
        }
    ], [groups, tags, t]);

    useEffect(() => {
        const orgId = state.organization?.id;
        if (!orgId) return;

        const load = async () => {
            const { groups, tags } = await ReportDataUtil.loadGroupsAndTags(orgId);
            setGroups(groups);
            setTags(tags);
        };

        load();
    }, [state.organization?.id]);

    useEffect(() => {
        if (!reportData) return;

        const filtered = applyFilters(reportData, filters, searchQuery);

        setFilteredReportData(filtered);


    }, [filters, reportData, searchQuery]);

    useEffect(() => {
        const orgId = state.organization?.id;
        if (!orgId) return;

        // Clear existing data immediately
        setReportData(null);
        setFilteredReportData(null);

        // reset UI state
        setFilters(DEFAULT_FILTERS);
        setSearchQuery("");

        const loadData = async () => {

            const data = await ReportDataUtil.grabInitialDynamicComplianceData(orgId);
            setReportData(data);
            setFilteredReportData(data);
        };

        loadData();
    }, [state.organization?.id]);

    useEffect(() => {
        if (reportData) {
            setIsLoading(false);
        }
        else {
            setIsLoading(true);
        }
    }, [reportData])

    const applyFilters = (
        reportData: ReportDynamicComplianceData,
        filters: FilterState,
        searchQuery: string
    ): ReportDynamicComplianceData => {
        return ReportDataUtil.filterInitialDynamicComplianceData(reportData, filters, searchQuery);
    };

    const handleExportCsv = () => {
        if (!reportData) return;

        const csvString = ReportDataUtil.generateCompliance({
            pendingAssessmentUsers: filteredReportData?.pendingAssessmentUsers?.map(u => u.displayText) || [],
            pendingRetest: filteredReportData?.pendingRetest?.map(u => u.displayText) || [],
            pendingLoginUsers: filteredReportData?.pendingLoginUsers?.map(u => u.displayText) || [],
            pendingUsers: filteredReportData?.pendingUsers || 0,
            usersAssessment: filteredReportData?.usersAssessment || 0,
            usersCompletedTasks: filteredReportData?.usersCompletedTasks || 0,
            usersLoggedIn: filteredReportData?.usersLoggedIn || 0,
            organizationName: reportData.organizationName,
            reportDate: DateTime.now().toISODate(),
            totalUsers: filteredReportData?.totalUsers || 0
        });

        const blob = new Blob(
            ["\uFEFF" + csvString], // UTF-8 BOM for Excel
            { type: "text/csv;charset=utf-8;" }
        );

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `${t('reports.compliance.fileReportName')}_${DateTime.now().toISODate()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };



    return (
        <div className={COMPONENT_CLASS}>
            <Loader isVisible={isLoading} />
            <div className={`${COMPONENT_CLASS}__header-container`}>
                <div className={`${COMPONENT_CLASS}__header`}>
                    <h1>{t('reportType.compliance_rep')}</h1>
                    <p className={`label-large ${COMPONENT_CLASS}__sub-header`}>{state.organization?.name} • {filteredReportData?.users.length} {t('general.users')}</p>
                </div>
                <div className={`${COMPONENT_CLASS}__actions`}>
                    <Button
                        onClick={handleExportCsv}
                        buttonText={t('buttons.btn_exportCSV')}
                        type="link" />
                    <Button
                        disabled={!filteredReportData}
                        onClick={() => ReportDataUtil.downloadCompliance(filteredReportData!, ReportDataUtil.buildReportFilterPayload(groups, tags, filters), state.organization?.id!, state.user?.id!)}
                        buttonText={t('buttons.btn_exportPDF')}
                        type="link" />
                    <ReportFilterMenu
                        filters={filterDefinitions}
                        values={filters}
                        onChange={(key, value) =>
                            setFilters(prev => ({
                                ...prev,
                                [key]: value
                            }))
                        }
                    >

                    </ReportFilterMenu>
                </div>
            </div>

            <div className={`${COMPONENT_CLASS}__summary`}>
                <div className={`${COMPONENT_CLASS}__summary-items`}>
                    <div className={`${COMPONENT_CLASS}__summary-item`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{completedAssessmentPercent + "%"}</span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('reports.compliance.completedAssessment')}</span>
                    </div>
                    <div className={`${COMPONENT_CLASS}__summary-item`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.usersAssessment}</span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('reports.compliance.completedAssessments')}</span>
                    </div>
                    <div className={`${COMPONENT_CLASS}__summary-item`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.pendingAssessmentUsers.length}</span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('reports.compliance.pendingAssessments')}</span>
                    </div>
                    <div className={`${COMPONENT_CLASS}__summary-item`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.pendingUsers}</span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('reports.compliance.pendingAccountCreation')}</span>
                    </div>
                    <div className={`${COMPONENT_CLASS}__summary-item`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.pendingRetest.length}</span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('reports.compliance.pendingRetest')}</span>
                    </div>
                </div>
            </div>

            <div className={`${COMPONENT_CLASS}__search-container`}>
                <input
                    className={`input-field ${COMPONENT_CLASS}__search-input`}
                    type="text"
                    placeholder={t('reports.filterOptions.searchByEmail')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            {
                filteredReportData?.pendingLoginUsers && (
                    <div className={`${COMPONENT_CLASS}__table-container`}>
                        <Accordion
                            isAccordionOpen={false}
                            title={
                                <div className="c-accordion-report-dynamic-grid__title-container">
                                    <CircleIcon sx={{ fontSize: 12 }} className="text-red-dark" />
                                    <h2 style={{ width: "auto" }}>{t('reports.compliance.pendingAccountCreation')}</h2>
                                    <span className="input-label bg-black-gray-5" style={{ padding: "4px", borderRadius: "4px" }}>
                                        {filteredReportData.pendingLoginUsers.length}
                                    </span>
                                </div>
                            }
                            customStyleClass="c-accordion-report-dynamic-grid"
                            content={
                                <ReportDynamicGrid
                                    users={filteredReportData.pendingLoginUsers}
                                    pageSize={24}
                                />
                            }
                        />
                    </div>
                )
            }

            {
                filteredReportData?.pendingAssessmentUsers && (
                    <div className={`${COMPONENT_CLASS}__table-container`}>
                        <Accordion
                            isAccordionOpen={false}
                            title={
                                <div className="c-accordion-report-dynamic-grid__title-container">
                                    <CircleIcon sx={{ fontSize: 12 }} className="text-yellow-dark" />
                                    <h2 style={{ width: "auto" }}>{t('reports.compliance.pendingAssessments')}</h2>
                                    <span className="input-label bg-black-gray-5" style={{ padding: "4px", borderRadius: "4px" }}>
                                        {filteredReportData.pendingAssessmentUsers.length}
                                    </span>
                                </div>
                            }
                            customStyleClass="c-accordion-report-dynamic-grid"
                            content={
                                <ReportDynamicGrid
                                    users={filteredReportData.pendingAssessmentUsers}
                                    pageSize={24}
                                />
                            }
                        />
                    </div>
                )
            }

            {
                filteredReportData?.pendingRetest && (
                    <div className={`${COMPONENT_CLASS}__table-container`}>
                        <Accordion
                            isAccordionOpen={false}
                            title={
                                <div className="c-accordion-report-dynamic-grid__title-container">
                                    <CircleIcon sx={{ fontSize: 12 }} className="text-blue-dark" />
                                    <h2 style={{ width: "auto" }}>{t('reports.compliance.pendingRetest')}</h2>
                                    <span className="input-label bg-black-gray-5" style={{ padding: "4px", borderRadius: "4px" }}>
                                        {filteredReportData.pendingRetest.length}
                                    </span>
                                </div>
                            }
                            customStyleClass="c-accordion-report-dynamic-grid"
                            content={
                                <ReportDynamicGrid
                                    users={filteredReportData.pendingRetest}
                                    pageSize={24}
                                />
                            }
                        />
                    </div>
                )
            }

        </div>
    );
}


export default ReportComplianceDynamic;
